import { Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
    ActiveGridCellDto,
    AirspaceAuthorisationDto,
    AirspaceAuthorisationResponse,
    AirspaceCheckDto,
    AirspaceCheckResponse,
    DateFormatService,
    DisplayableMissionDto,
    FlyFreelyError,
    FlyFreelyLoggingService,
    NameValue,
    OperationAuthorisation,
    Organisation,
    PersonDto,
    PersonRolesDto,
    PersonService,
    RpaTypeDto,
    RpaTypesService,
    RuleOutcome,
    SimpleAuthorityDto,
    StateUpdateCommand,
    UserService,
    patchState,
    patchableState,
    setState
} from '@flyfreely-portal-ui/flyfreely';
import { deepEqual } from 'fast-equals';
import {
    Observable,
    ReplaySubject,
    Subject,
    combineLatest,
    startWith
} from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    takeUntil
} from 'rxjs/operators';
import { AirspaceAuthorisationManager } from '../airspace-authorisation-manager.service';
import { specialValidationKeys } from '../helpers';
import { CasaFlightProfiles, CraftWithError } from '../interfaces';
import {
    combineAuthorisationErrors,
    combineAuthorisationErrorsForOperation,
    combineOperationAuthorisationList,
    findPilotValues,
    findTimeValues
} from './helpers';
import {
    acknowledgementValidator,
    activeGridCellListValidator,
    airspaceValidator,
    delegatedAuthorityValidator,
    operationAuthorisationsErrorsValidator,
    plannedMaxHeightValidator,
    responseErrorsValidator,
    rpaErrorsValidator,
    rpaListLengthValidator,
    rpicValidator
} from './validators';

export const delegatedAuthorityLabel = {
    [AirspaceAuthorisationDto.AuthorisationType.AUS_ASA_FIMS]: 'ReOC',
    [AirspaceAuthorisationDto.AuthorisationType.AUS_CASA_AUTHORISATION]: 'ReOC',
    [AirspaceAuthorisationDto.AuthorisationType.NZL_AIRSHARE]:
        'Delegated Authority',
    [AirspaceAuthorisationDto.AuthorisationType.USA_LAANC]:
        'Delegated Authority'
};

export const justificationLabel = {
    [AirspaceAuthorisationDto.AuthorisationType.AUS_ASA_FIMS]: 'Justification',
    [AirspaceAuthorisationDto.AuthorisationType.AUS_CASA_AUTHORISATION]:
        'Justification',
    [AirspaceAuthorisationDto.AuthorisationType.NZL_AIRSHARE]: 'Justification',
    [AirspaceAuthorisationDto.AuthorisationType.USA_LAANC]: 'Safety Case'
};

// TODO: this can be API driven, or at least feed from sysadmin
const declarationText = {
    [AirspaceAuthorisationDto.AuthorisationType.AUS_ASA_FIMS]: [],
    [AirspaceAuthorisationDto.AuthorisationType.AUS_CASA_AUTHORISATION]: [],
    [AirspaceAuthorisationDto.AuthorisationType.NZL_AIRSHARE]: [],
    [AirspaceAuthorisationDto.AuthorisationType.USA_LAANC]: [
        'I am authorised to make this application and hold the role of Chief Remote Pilot for the ReOC.',
        'I acknowledge I must not submit an airspace authorisation application for an RPA with a maximum take-off weight of 25kg or more.',
        'I declare that all statements in this application are true and correct in every particular and that I have read and understood all provisions of the Civil Aviation Safety Regulations 1998 which are relevant to this application.'
    ]
};

/**
 * A list of which form fields should be visible for each authorisation type.
 */
export const visibleAuthorisationFieldsByType = {
    [AirspaceAuthorisationDto.AuthorisationType.AUS_CASA_AUTHORISATION]: [
        'activeGridCellList',
        'authorisationMaxHeight',
        'plannedMaxHeight',
        'flightProfile',
        'civilTwilightStart',
        'civilTwilightEnd',
        'startTime',
        'endTime',
        'endRangeString',
        'rpic',
        'rpicAuthLabel',
        'rpicArn',
        'rpicPhone',
        'crp',
        'crpAuthLabel',
        'crpArn',
        'delegatedAuthority',
        'airspace'
    ],
    [AirspaceAuthorisationDto.AuthorisationType.NZL_AIRSHARE]: [
        'plannedMaxHeight',
        'publicMissionName',
        'startTime',
        'endTime',
        'endRangeString',
        'rpic',
        'rpicPhone',
        'additionalFields',
        'rpa',
        'delegatedAuthority'
    ],
    [AirspaceAuthorisationDto.AuthorisationType.USA_LAANC]: [
        'authorisationList',
        'plannedMaxHeight',
        'startTime',
        'endTime',
        'endRangeString',
        'rpic',
        'rpicPhone',
        'rpa',
        'delegatedAuthority',
        'safetyJustification',
        'warningsAndBlocks'
    ]
};

export const authorisationTypeCanEditMissionInPreview = {
    [AirspaceAuthorisationDto.AuthorisationType.AUS_ASA_FIMS]: false,
    [AirspaceAuthorisationDto.AuthorisationType.AUS_CASA_AUTHORISATION]: true,
    [AirspaceAuthorisationDto.AuthorisationType.NZL_AIRSHARE]: false,
    [AirspaceAuthorisationDto.AuthorisationType.USA_LAANC]: false
};

export const authorisationTypesAllowingMultipleFlightAreas = {
    [AirspaceAuthorisationDto.AuthorisationType.AUS_ASA_FIMS]: false,
    [AirspaceAuthorisationDto.AuthorisationType.AUS_CASA_AUTHORISATION]: true,
    [AirspaceAuthorisationDto.AuthorisationType.NZL_AIRSHARE]: false,
    [AirspaceAuthorisationDto.AuthorisationType.USA_LAANC]: false
};

export const airspaceAuthorisationFormErrors = {
    activeGridCellList: 'no valid active grid cell list',
    plannedMaxHeight: 'mission needs a planned maximum height',
    flightProfile: 'invalid or missing flight profile',
    rpaList: 'at least one valid RPA is required',

    rpic: 'invalid pilot details',
    rpicPhone: 'pilot phone number is invalid or missing',
    rpicArn: 'invalid or missing pilot licence number',
    rpicAuthLabel: 'Invalid or missing pilot licence authority',
    crp: 'the organisation requires at least one person with the CRP role',
    crpArn: 'invalid or missing CRP licence number',
    crpAuthLabel: 'invalid or missing CRP licence authority',

    delegatedAuthority: `there is a problem with the mission's delegated authority`,

    airspaceValidation:
        'the airspace is not good to fly for this authorisation type',

    startTime: 'the mission start time is invalid',
    endTime: 'the mission end time is invalid',
    warningsAndBlocks:
        'there are blockers or unaccepted warnings for this request',
    safetyJustification: 'a safety case is required for this request'
};

export const authorisationTypesWithOptions = [
    AirspaceAuthorisationDto.AuthorisationType.NZL_AIRSHARE
];

export const airshareFlightPurposes: NameValue[] = [
    { name: 'Recreational', value: 'RECREATIONAL' },
    { name: 'Commercial Photo/Video', value: 'COMMERCIAL_PHOTO_VIDEO' },
    { name: 'Agriculture', value: 'AGRICULTURE' },
    { name: 'Surveillance', value: 'SURVEILLANCE' },
    { name: 'Survey Mapping', value: 'SURVEY_MAPPING' },
    { name: 'Infrastructure Inspection', value: 'INFRASTRUCTURE_INSPECTION' },
    { name: 'Inventory Management', value: 'INVENTORY_MANAGEMENT' },
    { name: 'Construction Monitoring', value: 'CONSTRUCTION_MONITORING' },
    { name: 'Demonstration', value: 'DEMONSTRATION' },
    { name: 'Training', value: 'TRAINING' },
    { name: 'Fishing', value: 'FISHING' },
    { name: 'Other', value: 'OTHER' }
];

export interface AirspaceWarningsAndBlocksMessages {
    status: RuleOutcome.Outcome;
    name: string;
    message: string;
    acknowledged: boolean;
    relatedMissionIdList: number[];
}

export interface AirspaceAuthorisationDeclaration {
    acknowledged: boolean;
    text: string;
}

export interface AirspaceWarningsAndBlocksMessagesForm {
    status: FormControl<RuleOutcome.Outcome>;
    name: FormControl<string>;
    message: FormControl<string>;
    acknowledged: FormControl<boolean>;
    relatedMissionIdList: FormControl<number[]>;
}

export interface AirspaceAuthorisationDeclarationForm {
    acknowledged: FormControl<boolean>;
    text: FormControl<string>;
}

export interface AirspaceAuthorisationForm {
    authorisationList: FormControl<OperationAuthorisation[]>;
    publicMissionName: FormControl<string>;
    activeGridCellList: FormControl<ActiveGridCellDto[]>;
    plannedMaxHeight: FormControl<number>;
    // approvedMaxHeight: number; - Not sure if needed
    authorisationMaxHeight: FormControl<number>;
    flightProfile: FormControl<CasaFlightProfiles>;
    rpaList: FormControl<CraftWithError[]>;

    rpic: FormControl<PersonDto>;
    rpicPhone: FormControl<string>;
    rpicArn: FormControl<string>;
    rpicAuthLabel: FormControl<string>;
    crp: FormControl<PersonDto>;
    crpArn: FormControl<string>;
    crpAuthLabel: FormControl<string>;

    delegatedAuthority: FormControl<SimpleAuthorityDto>;

    airspaceValidation: FormControl<string>;

    startTime: FormControl<string>;
    // The validator controls are for async validation and serve the times as ISO strings
    validatorStartTime: FormControl<string>;
    endTime: FormControl<string>;
    validatorEndTime: FormControl<string>;
    startRangeString: FormControl<string>;
    endRangeString: FormControl<string>;
    timeZone: FormControl<string>;
    civilTwilightStart: FormControl<string>;
    validatorCivilTwilightStart: FormControl<string>;
    civilTwilightEnd: FormControl<string>;
    validatorCivilTwilightEnd: FormControl<string>;
    civilTwilight: FormControl<string>;

    authorisationErrors: FormControl<any>;

    // A collection of all airspace check advises and blocks
    // The object keys are the rule codes (RuleOutcome.code)
    warningsAndBlocks: FormArray<
        FormGroup<AirspaceWarningsAndBlocksMessagesForm>
    >;
    declarations: FormArray<FormGroup<AirspaceAuthorisationDeclarationForm>>;
    // A field to track if the authorisation preview still has any errors
    // This will primarily be used to lock the submit button and determine how to display the errors
    hasRemainingValidationErrors: FormControl<boolean>;
    safetyJustification: FormControl<string>;
}

export interface AirspaceAuthorisationFormValue {
    authorisationList: OperationAuthorisation[];
    publicMissionName: string;
    activeGridCellList: ActiveGridCellDto[];
    plannedMaxHeight: number;
    // approvedMaxHeight: number; - Not sure if needed
    authorisationMaxHeight: number;
    flightProfile: CasaFlightProfiles;
    rpaList: CraftWithError[];

    rpic: PersonDto;
    rpicPhone: string;
    rpicArn: string;
    rpicAuthLabel: string;
    crp: PersonDto;
    crpArn: string;
    crpAuthLabel: string;

    delegatedAuthority: SimpleAuthorityDto;

    airspaceValidation: string;

    startTime: string;
    // The validator controls are for async validation and serve the times as ISO strings
    validatorStartTime: string;
    endTime: string;
    validatorEndTime: string;
    startRangeString: string;
    endRangeString: string;
    timeZone: string;
    civilTwilightStart: string;
    validatorCivilTwilightStart: string;
    civilTwilightEnd: string;
    validatorCivilTwilightEnd: string;
    civilTwilight: string;

    authorisationErrors: any;

    // A collection of all airspace check advises and blocks
    // The object keys are the rule codes (RuleOutcome.code)
    warningsAndBlocks: AirspaceWarningsAndBlocksMessages[];
    declarations: AirspaceAuthorisationDeclaration[];
    // A field to track if the authorisation preview still has any errors
    // This will primarily be used to lock the submit button and determine how to display the errors
    hasRemainingValidationErrors: boolean;
    safetyJustification: string;
}

// ** State Interface **

/**
 * All external input data the service needs from the hosting screen and async
 * fetches. Using patchableState means setInputData can update organisation and
 * mission immediately while RPA types and CRP arrive asynchronously.
 */
interface PreviewInputState {
    mission: DisplayableMissionDto | null;
    organisation: Organisation | null;
    rpaTypes: RpaTypeDto[] | null;
    crp: PersonRolesDto | null;
    temporaryRpicPhone: string | null;
}

const INITIAL_PREVIEW_INPUT_STATE: PreviewInputState = {
    mission: null,
    organisation: null,
    rpaTypes: null,
    crp: null,
    temporaryRpicPhone: null
};

/**
 * Manages the reactive form state for the airspace authorisation preview UI.
 *
 * State is held in a single `patchableState` (`previewInputState$`) that
 * accumulates input from the hosting screen and async fetches. A consolidated
 * `combineLatest` pipeline watches both this state and the manager's observables;
 * it only proceeds when all required data is present, then selects the correct
 * form group for the active jurisdiction, populates it via `setupFormValues`,
 * and emits the ready form via `authorisationForm$`.
 *
 * The component subscribes to `authorisationForm$` rather than reading the
 * `authorisationForm` property directly, ensuring it always receives the form
 * after setup has completed.
 */
@Injectable()
export class AirspaceAuthorisationPreviewService {
    /**
     * Single form group shared across all jurisdictions. Validators are
     * applied per-jurisdiction by `applyValidatorsForType` whenever the active
     * type changes.
     */
    authorisationForm: FormGroup<AirspaceAuthorisationForm>;

    justificationRequired = false;

    // ** State **

    private previewInputStateSource = new Subject<
        StateUpdateCommand<PreviewInputState>
    >();
    private previewInputState$ = patchableState(
        this.previewInputStateSource,
        INITIAL_PREVIEW_INPUT_STATE
    );

    // ── Derived public observables ────────────────────────────────────────────

    readonly currentAuthorisationType$: Observable<AirspaceCheckDto.AvailableAutomatedAuthorisationList>;

    readonly temporaryRpicPhone$ = this.previewInputState$.pipe(
        map(s => s.temporaryRpicPhone),
        distinctUntilChanged(),
        shareReplay(1)
    );

    /**
     * Emits the active form group after it has been fully populated by
     * `setupFormValues`, together with the authorisation type it was built
     * for. The component subscribes to this rather than reading
     * `authorisationForm` directly, eliminating the timing race where the
     * component could read a stale or null form reference.
     */
    private authorisationFormSource = new ReplaySubject<{
        form: FormGroup<AirspaceAuthorisationForm>;
        type: AirspaceAuthorisationDto.AuthorisationType;
    } | null>(1);
    readonly authorisationForm$ = this.authorisationFormSource.pipe(
        map(v => v?.form ?? null)
    );
    /**
     * Same emission as `authorisationForm$`, but also carries the
     * authorisation type the form was built for so consumers can read both
     * values atomically — avoiding a race with `currentAuthorisationType$`,
     * whose notification order relative to this emission isn't guaranteed.
     */
    readonly authorisationFormReady$ =
        this.authorisationFormSource.asObservable();

    /**
     * The most recent AirspaceCheckResponse, kept in sync by the constructor
     * pipeline. Used by `setupWarningsAndBlocks` and `setupDeclarationText`.
     */
    private _currentCheckResponse: AirspaceCheckResponse | null = null;

    /**
     * The most recent active authorisation type, kept in sync by the
     * constructor pipeline. Used by `setupJustificationRequirements` and
     * `setupDeclarationText`.
     */
    private _currentAuthorisationType: AirspaceAuthorisationDto.AuthorisationType | null =
        null;

    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private airspaceAuthorisationManager: AirspaceAuthorisationManager,
        private personService: PersonService,
        private userService: UserService,
        private rpaTypesService: RpaTypesService,
        private dateFormatService: DateFormatService,
        private logging: FlyFreelyLoggingService
    ) {
        /**
         * The active authorisation type, derived from the manager. Replaces the
         * former `currentAuthorisationTypeSource` BehaviorSubject.
         */
        this.currentAuthorisationType$ =
            this.airspaceAuthorisationManager.availableAirspaceAuthorisations$.pipe(
                map(types => types[0] ?? null),
                distinctUntilChanged(),
                shareReplay(1)
            );

        // Seed state so previewInputState$ emits immediately for combineLatest.
        this.previewInputStateSource.next(
            setState(INITIAL_PREVIEW_INPUT_STATE)
        );

        // ** Form group without validators **
        //
        // All controls start with no validators. `applyValidatorsForType` is
        // called each time the active jurisdiction changes.
        this.authorisationForm = new FormGroup<AirspaceAuthorisationForm>({
            authorisationList: new FormControl(undefined),
            publicMissionName: new FormControl(undefined),
            activeGridCellList: new FormControl(undefined),
            authorisationMaxHeight: new FormControl(undefined),
            plannedMaxHeight: new FormControl(undefined),
            flightProfile: new FormControl(undefined),
            rpaList: new FormControl(undefined),
            rpic: new FormControl(undefined),
            rpicPhone: new FormControl(undefined),
            rpicArn: new FormControl(undefined),
            rpicAuthLabel: new FormControl(undefined),
            crp: new FormControl(undefined),
            crpArn: new FormControl(undefined),
            crpAuthLabel: new FormControl(undefined),
            delegatedAuthority: new FormControl(undefined),
            startTime: new FormControl(undefined),
            validatorStartTime: new FormControl(undefined),
            endTime: new FormControl(undefined),
            validatorEndTime: new FormControl(undefined),
            startRangeString: new FormControl(undefined),
            endRangeString: new FormControl(undefined),
            timeZone: new FormControl(undefined),
            civilTwilightStart: new FormControl(undefined),
            validatorCivilTwilightStart: new FormControl(undefined),
            civilTwilightEnd: new FormControl(undefined),
            validatorCivilTwilightEnd: new FormControl(undefined),
            civilTwilight: new FormControl(undefined),
            airspaceValidation: new FormControl(undefined),
            authorisationErrors: new FormControl(undefined),
            warningsAndBlocks: new FormArray([]),
            declarations: new FormArray([]),
            safetyJustification: new FormControl(undefined),
            hasRemainingValidationErrors: new FormControl(undefined)
        });

        combineLatest([
            this.airspaceAuthorisationManager.airspaceCheckResult$,
            this.airspaceAuthorisationManager.availableAirspaceAuthorisations$,
            this.airspaceAuthorisationManager.airspaceAuthorisationPreview$.pipe(
                startWith(null)
            ),
            this.previewInputState$
        ])
            .pipe(
                filter(
                    ([check, types, _, input]) =>
                        check != null &&
                        types.length > 0 &&
                        input.mission != null &&
                        input.rpaTypes != null
                ),
                distinctUntilChanged(deepEqual),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(([check, types, preview, input]) => {
                // Update cached fields for public methods that need synchronous
                // access (setupWarningsAndBlocks, setupDeclarationText).
                this._currentCheckResponse =
                    check.result as unknown as AirspaceCheckResponse;
                this._currentAuthorisationType = types[0];

                // Apply validators for the active jurisdiction type, then
                // populate the form.
                this.applyValidatorsForType(types[0]);

                this.setupFormValues(preview, check, input);

                // Emit the fully-populated form, together with the type it
                // was built for, so the component can react to both atomically.
                this.authorisationFormSource.next({
                    form: this.authorisationForm,
                    type: types[0]
                });
            });
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.previewInputStateSource.complete();
        this.authorisationFormSource.complete();
    }

    setInputData(mission: DisplayableMissionDto, organisation: Organisation) {
        // Reset rpaTypes and crp to null so the pipeline filter rejects the
        // partial state while the new fetches are in flight.
        this.previewInputStateSource.next(
            patchState({ mission, organisation, rpaTypes: null, crp: null })
        );
        this.rpaTypesService
            .findRpaTypes(organisation.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(rpaTypes =>
                this.previewInputStateSource.next(patchState({ rpaTypes }))
            );
        this.checkIfCurrentUserIsCrp(organisation);
    }

    /**
     * Override the RPIC phone number temporarily without re-running the full
     * preview fetch.
     */
    updateRpicTemporaryPhoneNumber(temporaryNumber: string) {
        if (
            this.authorisationForm.controls.rpicPhone.value === temporaryNumber
        ) {
            return;
        }
        this.previewInputStateSource.next(
            patchState({ temporaryRpicPhone: temporaryNumber })
        );
        this.authorisationForm.patchValue({ rpicPhone: temporaryNumber });
        this.authorisationForm.updateValueAndValidity();
    }

    setupJustificationRequirements(
        authorisation: AirspaceAuthorisationResponse
    ) {
        if (authorisation == null) {
            return;
        }
        if (
            authorisation.authorisationList?.length > 0 &&
            authorisation.authorisationList.filter(
                a =>
                    a.authorisationEligibility ===
                    OperationAuthorisation.AuthorisationEligibility.MANUAL
            ).length > 0
        ) {
            this.authorisationForm.controls.safetyJustification.setValidators(
                Validators.required
            );
            this.justificationRequired = true;
        } else {
            this.authorisationForm.controls.safetyJustification.clearValidators();
            this.justificationRequired = false;
        }
        this.authorisationForm.controls.safetyJustification.updateValueAndValidity();
    }

    setupWarningsAndBlocks(authorisation: AirspaceAuthorisationResponse) {
        // first clear out all existing form array data
        while (
            this.authorisationForm.controls.warningsAndBlocks.value.length > 0
        ) {
            this.authorisationForm.controls.warningsAndBlocks.removeAt(0);
        }

        if (authorisation == null) {
            return;
        }

        // create new warnings and blocks
        const checkResponse = this._currentCheckResponse;
        const authorisationRuleOutcomes =
            authorisation.authorisationRuleOutcomeList;
        if (
            checkResponse == null ||
            (checkResponse.ruleOutcomes == null &&
                authorisationRuleOutcomes == null)
        ) {
            return;
        }
        const authorisationWarnings = authorisationRuleOutcomes.filter(
            r => r.outcome === RuleOutcome.Outcome.ADVISE
        );
        const checkerWarnings = checkResponse?.ruleOutcomes?.filter(
            r => r.outcome === RuleOutcome.Outcome.ADVISE
        );
        const authorisationBlocks = authorisationRuleOutcomes.filter(
            r => r.outcome === RuleOutcome.Outcome.BLOCK
        );
        const checkerBlocks = checkResponse?.ruleOutcomes?.filter(
            r => r.outcome === RuleOutcome.Outcome.BLOCK
        );
        const warningsAndBlocksMessages: AirspaceWarningsAndBlocksMessages[] =
            [];
        authorisationBlocks.forEach(block =>
            warningsAndBlocksMessages.push({
                acknowledged: false,
                status: block.outcome,
                name: block.title,
                message: block.message,
                relatedMissionIdList: block.relatedMissionIdList
            })
        );
        checkerBlocks.forEach(block =>
            warningsAndBlocksMessages.push({
                acknowledged: false,
                status: block.outcome,
                name: checkResponse.ruleMessages[block.code].name,
                message: checkResponse.ruleMessages[block.code].blockMessage,
                relatedMissionIdList: null
            })
        );
        authorisationWarnings.forEach(warning =>
            warningsAndBlocksMessages.push({
                acknowledged: false,
                status: warning.outcome,
                name: warning.title,
                message: warning.message,
                relatedMissionIdList: warning.relatedMissionIdList
            })
        );
        checkerWarnings.forEach(warning =>
            warningsAndBlocksMessages.push({
                acknowledged: false,
                status: warning.outcome,
                name: checkResponse.ruleMessages[warning.code].name,
                message: checkResponse.ruleMessages[warning.code].adviseMessage,
                relatedMissionIdList: null
            })
        );

        // Build the form array
        warningsAndBlocksMessages.forEach(message =>
            this.authorisationForm.controls.warningsAndBlocks.push(
                new FormGroup<AirspaceWarningsAndBlocksMessagesForm>({
                    acknowledged: new FormControl(message.acknowledged, [
                        acknowledgementValidator()
                    ]),
                    status: new FormControl(message.status),
                    name: new FormControl(message.name),
                    message: new FormControl(message.message),
                    relatedMissionIdList: new FormControl(
                        message.relatedMissionIdList
                    )
                })
            )
        );
    }

    setupDeclarationText() {
        // first clear out all existing form array data
        while (this.authorisationForm.controls.declarations.value.length > 0) {
            this.authorisationForm.controls.declarations.removeAt(0);
        }

        // create new warnings and blocks
        const declarations = declarationText[this._currentAuthorisationType];
        if (declarations == null || declarations.length === 0) {
            return;
        }

        // Build the form array
        declarations.forEach(declaration =>
            this.authorisationForm.controls.declarations.push(
                new FormGroup<AirspaceAuthorisationDeclarationForm>({
                    acknowledged: new FormControl(false, [
                        acknowledgementValidator()
                    ]),
                    text: new FormControl(declaration)
                })
            )
        );
    }

    /**
     * Clear all sync and async validators from every `FormControl` in
     * `authorisationForm`, then apply the set appropriate for `type`.
     */
    private applyValidatorsForType(
        type: AirspaceAuthorisationDto.AuthorisationType
    ) {
        const c = this.authorisationForm.controls;

        // ── 1. Clear all existing validators ─────────────────────────────────
        (Object.keys(c) as (keyof AirspaceAuthorisationForm)[]).forEach(key => {
            const ctrl = c[key];
            if (ctrl instanceof FormControl) {
                ctrl.clearValidators();
                ctrl.clearAsyncValidators();
            }
        });

        // ── 2. Apply type-specific validators ────────────────────────────────
        switch (type) {
            case AirspaceAuthorisationDto.AuthorisationType
                .AUS_CASA_AUTHORISATION:
                // Sync
                c.activeGridCellList.setValidators([Validators.required]);
                c.plannedMaxHeight.setValidators([Validators.required]);
                c.flightProfile.setValidators([Validators.required]);
                c.rpaList.setValidators([
                    Validators.required,
                    rpaListLengthValidator()
                ]);
                c.rpic.setValidators([Validators.required]);
                c.rpicPhone.setValidators([Validators.required]);
                c.rpicArn.setValidators([Validators.required]);
                c.crp.setValidators([Validators.required]);
                c.crpArn.setValidators([Validators.required]);
                c.startTime.setValidators([Validators.required]);
                c.endTime.setValidators([Validators.required]);
                c.airspaceValidation.setValidators([Validators.required]);
                // Async
                c.activeGridCellList.setAsyncValidators([
                    activeGridCellListValidator()
                ]);
                c.plannedMaxHeight.setAsyncValidators([
                    plannedMaxHeightValidator()
                ]);
                c.rpaList.setAsyncValidators([rpaErrorsValidator()]);
                c.rpic.setAsyncValidators([rpicValidator()]);
                c.delegatedAuthority.setAsyncValidators([
                    delegatedAuthorityValidator(),
                    responseErrorsValidator('delegatedAuthority')
                ]);
                c.startTime.setAsyncValidators([
                    responseErrorsValidator('startTime')
                ]);
                c.endTime.setAsyncValidators([
                    responseErrorsValidator('endTime')
                ]);
                c.civilTwilight.setAsyncValidators([
                    responseErrorsValidator('startTime'),
                    responseErrorsValidator('endTime')
                ]);
                c.airspaceValidation.setAsyncValidators([airspaceValidator()]);
                break;

            case AirspaceAuthorisationDto.AuthorisationType.NZL_AIRSHARE:
                // Sync
                c.publicMissionName.setValidators([Validators.required]);
                c.plannedMaxHeight.setValidators([Validators.required]);
                c.rpaList.setValidators([
                    Validators.required,
                    rpaListLengthValidator()
                ]);
                c.rpic.setValidators([Validators.required]);
                c.delegatedAuthority.setValidators([Validators.required]);
                c.startTime.setValidators([Validators.required]);
                c.endTime.setValidators([Validators.required]);
                // Async
                c.rpaList.setAsyncValidators([
                    responseErrorsValidator('rpaList')
                ]);
                c.rpic.setAsyncValidators([
                    responseErrorsValidator('rpic'),
                    responseErrorsValidator('remotePilot'),
                    responseErrorsValidator('remotePilotRegistrationDetails')
                ]);
                c.delegatedAuthority.setAsyncValidators([
                    responseErrorsValidator('delegatedAuthority')
                ]);
                c.startTime.setAsyncValidators([
                    responseErrorsValidator('startTime')
                ]);
                c.endTime.setAsyncValidators([
                    responseErrorsValidator('endTime')
                ]);
                break;

            case AirspaceAuthorisationDto.AuthorisationType.USA_LAANC:
                // Sync
                c.authorisationList.setValidators([
                    operationAuthorisationsErrorsValidator()
                ]);
                c.rpaList.setValidators([Validators.required]);
                c.rpic.setValidators([Validators.required]);
                c.startTime.setValidators([Validators.required]);
                c.endTime.setValidators([Validators.required]);
                // Async
                c.rpaList.setAsyncValidators([
                    responseErrorsValidator('rpaList')
                ]);
                c.rpic.setAsyncValidators([
                    responseErrorsValidator('rpic'),
                    responseErrorsValidator('remotePilot'),
                    responseErrorsValidator('remotePilotPhoneNumber')
                ]);
                c.rpicPhone.setAsyncValidators([
                    responseErrorsValidator('remotePilotPhoneNumber')
                ]);
                c.crp.setAsyncValidators([responseErrorsValidator('crp')]);
                c.delegatedAuthority.setAsyncValidators([
                    delegatedAuthorityValidator(),
                    responseErrorsValidator('delegatedAuthority')
                ]);
                c.startTime.setAsyncValidators([
                    responseErrorsValidator('startTime')
                ]);
                c.endTime.setAsyncValidators([
                    responseErrorsValidator('endTime')
                ]);
                break;
        }

        // Trigger re-validation with the new validators
        (Object.keys(c) as (keyof AirspaceAuthorisationForm)[]).forEach(key => {
            const ctrl = c[key];
            if (ctrl instanceof FormControl) {
                ctrl.updateValueAndValidity({ emitEvent: false });
            }
        });
    }

    /**
     * Reset the single form and populate it from the provided data.
     * All data is received as parameters so there are no getValue() calls.
     */
    private setupFormValues(
        authorisation: AirspaceAuthorisationResponse | null,
        check: {
            check: import('@flyfreely-portal-ui/flyfreely').AirspaceCheckCommand;
            result: AirspaceCheckDto;
            missionId?: number;
        },
        input: PreviewInputState
    ) {
        // Reset controls but preserve the warningsAndBlocks and declarations
        // FormArrays — their rows are rebuilt by setupWarningsAndBlocks.
        this.authorisationForm.reset(undefined, { emitEvent: false });

        const { mission, rpaTypes, crp: rcp, temporaryRpicPhone } = input;
        const authorisationType = this._currentAuthorisationType;
        const checkResponse = check.result as unknown as AirspaceCheckResponse;
        const activeOperations = combineOperationAuthorisationList(
            authorisation?.authorisationList
        );

        if (authorisationType == null) {
            return;
        }

        // Get field values
        const {
            rpic,
            rpicPhone,
            rpicArn,
            rpicAuthLabel,
            crp,
            crpArn,
            crpAuthLabel
        } = findPilotValues(mission, rcp);

        const {
            timeZone,
            startTime,
            validatorStart,
            endTime,
            validatorEnd,
            startRangeString,
            endRangeString,
            civilTwilightStart,
            validatorCivilTwilightStart,
            civilTwilightEnd,
            validatorCivilTwilightEnd
        } = findTimeValues(mission, checkResponse, this.dateFormatService);

        const airspaceValidation =
            (activeOperations?.errors?.['airspace'] ??
                authorisation?.errors?.['airspace']) != null
                ? null
                : 'Airspace clear to fly';

        const delegatedAuthority =
            mission?.missionWorkflowVersion.delegatedAuthority.authorityType
                .ruleset != null
                ? mission?.missionWorkflowVersion.delegatedAuthority
                : null;

        const rpaList = mission.crafts?.map((c, i) => ({
            ...c,
            missingMtow: false
        }));

        const mtowErrors = Object.keys(authorisation?.errors ?? {}).filter(k =>
            k.includes('rpaType.performanceSpecifications.maxTakeOffWeight')
        );
        if (mtowErrors != null && mtowErrors.length > 0) {
            const craftMissingMtowIds = mtowErrors.map(k =>
                parseInt(k.slice(k.indexOf('[') + 1, k.indexOf(']')))
            );
            craftMissingMtowIds.forEach(id => (rpaList[id].missingMtow = true));
        }

        // A submission is blocked if *any* authorisation entry currently has
        // remaining errors. `combineAuthorisationErrors` aggregates the
        // top-level errors with every `authorisationList` entry's errors
        // (equivalent to combining with the already-combined activeOperation
        // below, since that's every entry reduced into one).
        const combinedErrors = combineAuthorisationErrors(authorisation);
        const hasRemainingValidationErrors =
            Object.keys(combinedErrors ?? {}).filter(
                key => !specialValidationKeys.includes(key)
            ).length > 0;

        this.authorisationForm.patchValue({
            // For now all the authorisation errors are combined into one
            // field to simplify validation. Sourced from the mission-level
            // errors plus every authorisation entry's own errors (already
            // combined into activeOperation above).
            authorisationErrors: combineAuthorisationErrorsForOperation(
                authorisation,
                activeOperations
            ),
            authorisationList: authorisation?.authorisationList,

            // Mission operations & location details
            // FIXME: need to restore this
            // activeGridCellList: authorisation?.activeGridCellList,
            // authorisationMaxHeight: authorisation?.maximumHeight,
            publicMissionName: mission?.name,
            // FIXME: Ensure these values are correct for CASA and Airshare
            activeGridCellList: activeOperations?.activeGridCellList,
            authorisationMaxHeight: activeOperations?.maximumHeight,
            plannedMaxHeight: mission.maximumHeight,
            flightProfile: undefined,
            rpaList: rpaList.length > 0 ? rpaList : null,

            // Personnel & authorities
            rpicAuthLabel: rpicAuthLabel,
            rpicArn: rpicArn,
            rpicPhone: temporaryRpicPhone ?? rpicPhone,
            rpic: rpic,
            crpAuthLabel: crpAuthLabel,
            crpArn: crpArn,
            crp: crp,
            delegatedAuthority: delegatedAuthority,

            // Time values
            validatorStartTime: validatorStart,
            validatorEndTime: validatorEnd,
            validatorCivilTwilightStart: validatorCivilTwilightStart,
            validatorCivilTwilightEnd: validatorCivilTwilightEnd,

            timeZone: timeZone,
            startTime: startTime,
            endTime: endTime,
            startRangeString: startRangeString,
            endRangeString: endRangeString,
            civilTwilightStart: civilTwilightStart,
            civilTwilightEnd: civilTwilightEnd,
            civilTwilight: `${civilTwilightStart} - ${civilTwilightEnd}`,

            airspaceValidation: airspaceValidation,
            safetyJustification: null,

            hasRemainingValidationErrors: hasRemainingValidationErrors
        });

        if (
            authorisationType ===
            AirspaceAuthorisationDto.AuthorisationType.USA_LAANC
        ) {
            this.setupWarningsAndBlocks(authorisation);
            this.setupJustificationRequirements(authorisation);
        }

        this.authorisationForm.updateValueAndValidity();
    }

    /**
     * Checks if the current user is a CRP in the current org and patches the
     * CRP into previewInputState$ when the lookup completes.
     */
    private checkIfCurrentUserIsCrp(organisation: Organisation) {
        const currentUser = this.userService.getCurrentUser();
        this.personService
            .findPersonnelGraphQL(organisation.id, 0, 1, {
                idList: [currentUser.id]
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    if (
                        result != null &&
                        result.results != null &&
                        result.results[0]?.roles.includes(
                            PersonRolesDto.Roles.CRP
                        )
                    ) {
                        this.findCrpUsingId(result.results[0].id, organisation);
                    } else {
                        this.findOrganisationCrp(organisation);
                    }
                },
                error: (error: FlyFreelyError) =>
                    this.findOrganisationCrp(organisation)
            });
    }

    private findOrganisationCrp(organisation: Organisation) {
        this.personService
            .findPersonnelGraphQL(organisation.id, 0, 1, {
                roleOrList: [PersonRolesDto.Roles.CRP]
            })
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    if (result.results.length === 0) {
                        this.previewInputStateSource.next(
                            patchState({ crp: null })
                        );
                    } else {
                        this.findCrpUsingId(result.results[0].id, organisation);
                    }
                },
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while retrieving CRP details: ${error.message}`
                    )
            });
    }

    private findCrpUsingId(id: number, organisation: Organisation) {
        this.personService
            .findPerson(id, organisation.id)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(crp =>
                this.previewInputStateSource.next(patchState({ crp }))
            );
    }
}
